import React, { useState, useEffect, useCallback } from 'react';
import { Container, Button, Table, Modal, Form, Spinner, Alert, Row, Col } from 'react-bootstrap';
import apiClient, { API_ENDPOINTS } from '../utils/api';
import RecordCard from '../components/RecordCard';
import {
  calculateNights,
  formatCurrency,
  formatDate,
  formatPhone,
  getStatusColor,
  getStatusLabel,
  truncateText,
} from '../utils/helpers';

const initialBookingForm = {
  property_name: 'TnewT',
  booking_id: '',
  guest_name: '',
  guest_email: '',
  guest_phone: '',
  check_in_date: '',
  check_out_date: '',
  number_of_guests: 1,
  price_per_night: '',
  // 手動追加もオーナー承認フローの起点(pending)から始める。confirmed で作ると
  // 承認待ちを飛ばしてしまい、行の「チェックイン依頼」ボタンも出なかった。
  booking_status: 'pending',
  payment_status: 'pending',
  booking_source: '',
  option: '',
  passcode: '',
  cleaning_fee: '', // オーナー精算用の清掃費上書き。空なら精算設定の既定値(5,000円)
  notes: '',
};

function Bookings() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingBookingId, setEditingBookingId] = useState(null);
  const [filterStatus, setFilterStatus] = useState('');
  const [bookingForm, setBookingForm] = useState(initialBookingForm);
  const [showDetails, setShowDetails] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [viewMode, setViewMode] = useState('table'); // 'table' | 'calendar'
  const [sortKey, setSortKey] = useState('check_in_date');
  const [sortDir, setSortDir] = useState('desc');
  const [exportMonth, setExportMonth] = useState(''); // '' = 全期間
  const [calMonth, setCalMonth] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });

  const fetchBookings = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const params = filterStatus ? { status_filter: filterStatus } : {};
      const res = await apiClient.get(API_ENDPOINTS.BOOKINGS, { params });
      setBookings(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || '予約データの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, [filterStatus]);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  const nights = calculateNights(bookingForm.check_in_date, bookingForm.check_out_date);
  const totalPrice = Number(bookingForm.price_per_night || 0) * nights;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setBookingForm((prev) => ({ ...prev, [name]: value }));
  };

  const openCreateModal = () => {
    setEditingBookingId(null);
    setBookingForm(initialBookingForm);
    setShowDetails(false);
    setShowModal(true);
  };

  const openEditModal = (booking) => {
    const derivedPrice = booking.price_per_night ?? (
      booking.number_of_nights ? Number(booking.total_price || 0) / booking.number_of_nights : 0
    );
    setEditingBookingId(booking.id);
    setBookingForm({
      property_name: booking.property_name || '',
      booking_id: booking.booking_id || '',
      guest_name: booking.guest_name || '',
      guest_email: booking.guest_email || '',
      guest_phone: booking.guest_phone || '',
      check_in_date: booking.check_in_date ? booking.check_in_date.slice(0, 10) : '',
      check_out_date: booking.check_out_date ? booking.check_out_date.slice(0, 10) : '',
      number_of_guests: booking.number_of_guests || 1,
      price_per_night: String(derivedPrice || 0),
      booking_status: booking.booking_status || 'confirmed',
      payment_status: booking.payment_status || 'pending',
      booking_source: booking.booking_source || '',
      option: booking.option || '',
      passcode: booking.passcode || '',
      cleaning_fee: booking.cleaning_fee ?? '',
      notes: booking.notes || '',
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingBookingId(null);
    setBookingForm(initialBookingForm);
    setShowDetails(false);
  };

  const buildPayload = () => {
    const pricePerNight = Number(bookingForm.price_per_night || 0);
    const n = calculateNights(bookingForm.check_in_date, bookingForm.check_out_date);
    return {
      // 空欄は null = 上書き解除(精算設定の既定値に戻る)。0円は有効な上書き
      cleaning_fee: bookingForm.cleaning_fee === '' ? null : Number(bookingForm.cleaning_fee),
      property_name: bookingForm.property_name,
      booking_id: bookingForm.booking_id || null,
      guest_name: bookingForm.guest_name,
      guest_email: bookingForm.guest_email,
      guest_phone: bookingForm.guest_phone || null,
      check_in_date: bookingForm.check_in_date,
      check_out_date: bookingForm.check_out_date,
      number_of_guests: Number(bookingForm.number_of_guests),
      number_of_nights: n,
      price_per_night: pricePerNight,
      total_price: pricePerNight * n,
      booking_status: bookingForm.booking_status,
      payment_status: bookingForm.payment_status,
      booking_source: bookingForm.booking_source || null,
      option: bookingForm.option || null,
      passcode: bookingForm.passcode || null,
      notes: bookingForm.notes || null,
    };
  };

  // オーナー依頼の送信。日程重複(409)は確認のうえ force で送り直す。
  // 予約作成の force と対で、どちらか片方だけ通ると依頼だけ送れない状態になるため。
  const postCheckinRequest = async (bookingId) => {
    try {
      return await apiClient.post(API_ENDPOINTS.LINE_CHECKIN_REQUEST, { booking_id: bookingId });
    } catch (err) {
      if (err.response?.status !== 409) throw err;
      const detail = err.response?.data?.detail || '日程が重複しています。';
      if (!window.confirm(`${detail}

重複したままオーナーに依頼を送りますか？`)) throw err;
      return apiClient.post(API_ENDPOINTS.LINE_CHECKIN_REQUEST, { booking_id: bookingId, force: true });
    }
  };

  const handleSaveBooking = async (alsoRequest = false) => {
    setSaving(true);
    setError('');
    try {
      const payload = buildPayload();
      let bookingId = editingBookingId;
      if (editingBookingId) {
        await apiClient.put(`${API_ENDPOINTS.BOOKINGS}/${editingBookingId}`, payload);
        setMessage('予約を更新しました');
      } else {
        let res;
        let forced = false;
        try {
          res = await apiClient.post(`${API_ENDPOINTS.BOOKINGS}/`, payload);
        } catch (postErr) {
          if (postErr.response?.status !== 409) throw postErr;
          // ダブルブッキングは取り返しがつかないので、必ず本人に確認してから通す
          const detail = postErr.response?.data?.detail || '日程が重複しています。';
          if (!window.confirm(`${detail}

ダブルブッキングになりますが、このまま登録しますか？`)) {
            throw postErr;
          }
          res = await apiClient.post(`${API_ENDPOINTS.BOOKINGS}/?force=true`, payload);
          forced = true;
        }
        bookingId = res.data?.id;
        setMessage(forced ? '日程重複を承知のうえで予約を作成しました' : '予約を作成しました');
      }
      // 依頼の送信は保存とは別に扱う。まとめて catch すると、保存は済んでいるのに
      // 「予約の保存に失敗しました」と出て、どちらで失敗したのか分からなくなる。
      if (alsoRequest && bookingId) {
        try {
          const r = await postCheckinRequest(bookingId);
          setMessage(
            r.data?.shown_now
              ? '保存し、オーナーのLINEに承認依頼を表示しました'
              : '保存し、依頼を順番待ちに追加しました（前の承認が終わると自動表示）'
          );
        } catch (reqErr) {
          setMessage('');
          setError(
            `予約は保存できました。オーナー依頼だけ送信できませんでした：${
              reqErr.response?.data?.detail || '送信に失敗しました'
            }（一覧の「チェックイン依頼」から送り直せます）`
          );
        }
      }
      closeModal();
      await fetchBookings();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(
        detail
          ? `予約を保存できませんでした：${detail}（オーナー依頼は送信していません）`
          : '予約の保存に失敗しました（オーナー依頼は送信していません）'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleSendCheckinRequest = async (booking) => {
    setError('');
    setMessage('');
    try {
      const res = await postCheckinRequest(booking.id);
      setMessage(
        res.data?.shown_now
          ? 'オーナーのLINEに承認依頼を表示しました'
          : '依頼を順番待ちに追加しました（前の承認が終わると自動で表示されます）'
      );
      await fetchBookings();
    } catch (err) {
      setError(err.response?.data?.detail || 'チェックイン依頼の送信に失敗しました');
    }
  };

  const handleCancelBooking = async (booking) => {
    if (!window.confirm('この予約をキャンセルしますか？（承認済みの場合はGoogleカレンダーの予定も削除されます）')) {
      return;
    }
    setError('');
    setMessage('');
    try {
      await apiClient.post(`${API_ENDPOINTS.BOOKINGS}/${booking.id}/cancel`);
      setMessage('予約をキャンセルしました');
      await fetchBookings();
    } catch (err) {
      setError(err.response?.data?.detail || '予約のキャンセルに失敗しました');
    }
  };

  const handleDeleteBooking = async (bookingId) => {
    setError('');
    try {
      await apiClient.delete(`${API_ENDPOINTS.BOOKINGS}/${bookingId}`);
      setMessage('予約を削除しました');
      await fetchBookings();
    } catch (err) {
      setError(err.response?.data?.detail || '予約の削除に失敗しました');
    }
  };

  // 必須はゲスト名とチェックイン/アウトのみ。メール・料金などは任意(未入力は既定値で送信)。
  const canSave = (
    bookingForm.guest_name &&
    bookingForm.check_in_date &&
    bookingForm.check_out_date &&
    nights > 0
  );

  // 宿泊者台帳CSVエクスポート（旅館業法 宿泊者名簿）。月指定があればその月のみ。
  const handleExportCsv = async () => {
    setError('');
    setMessage('');
    try {
      const params = exportMonth ? { month: exportMonth } : {};
      const res = await apiClient.get(`${API_ENDPOINTS.BOOKINGS}/export`, { responseType: 'blob', params });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `宿泊者台帳_${exportMonth || new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      setMessage(exportMonth ? `${exportMonth} の宿泊者台帳CSVを出力しました` : '宿泊者台帳CSV（全期間）を出力しました');
    } catch (err) {
      setError('CSVの出力に失敗しました');
    }
  };

  // 並び替え
  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };
  const sortIndicator = (key) => (sortKey === key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '');
  const numericKeys = ['total_price', 'number_of_guests'];
  const sortedBookings = [...bookings].sort((a, b) => {
    const dir = sortDir === 'asc' ? 1 : -1;
    let va = a[sortKey];
    let vb = b[sortKey];
    if (numericKeys.includes(sortKey)) {
      va = Number(va || 0);
      vb = Number(vb || 0);
    } else {
      va = (va ?? '').toString().toLowerCase();
      vb = (vb ?? '').toString().toLowerCase();
    }
    if (va < vb) return -1 * dir;
    if (va > vb) return 1 * dir;
    return 0;
  });

  // カレンダー
  const monthLabel = `${calMonth.getFullYear()}年${calMonth.getMonth() + 1}月`;
  const changeMonth = (delta) => setCalMonth((m) => new Date(m.getFullYear(), m.getMonth() + delta, 1));
  const goThisMonth = () => {
    const d = new Date();
    setCalMonth(new Date(d.getFullYear(), d.getMonth(), 1));
  };
  const renderCalendar = () => {
    const year = calMonth.getFullYear();
    const month = calMonth.getMonth();
    const firstDow = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const byDay = {};
    bookings.forEach((b) => {
      const ci = (b.check_in_date || '').slice(0, 10);
      if (!ci) return;
      const parts = ci.split('-').map(Number);
      if (parts[0] === year && parts[1] === month + 1) {
        const day = parts[2];
        (byDay[day] = byDay[day] || []).push(b);
      }
    });
    const cells = [];
    for (let i = 0; i < firstDow; i += 1) cells.push(null);
    for (let d = 1; d <= daysInMonth; d += 1) cells.push(d);
    while (cells.length % 7 !== 0) cells.push(null);
    const weeks = [];
    for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
    const dow = ['日', '月', '火', '水', '木', '金', '土'];
    const monthCount = Object.values(byDay).reduce((n, arr) => n + arr.length, 0);
    return (
      <div>
        <div className="d-flex justify-content-between align-items-center mb-2">
          <Button variant="outline-secondary" size="sm" onClick={() => changeMonth(-1)}>← 前月</Button>
          <div className="text-center">
            <h5 className="mb-0">{monthLabel}</h5>
            <Button variant="link" size="sm" className="p-0" onClick={goThisMonth}>今月へ</Button>
          </div>
          <Button variant="outline-secondary" size="sm" onClick={() => changeMonth(1)}>翌月 →</Button>
        </div>
        <Table bordered responsive className="text-center mb-1" style={{ tableLayout: 'fixed' }}>
          <thead>
            <tr>
              {dow.map((w, i) => (
                <th key={w} style={{ color: i === 0 ? '#dc3545' : i === 6 ? '#0d6efd' : undefined }}>{w}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {weeks.map((week, wi) => (
              <tr key={wi}>
                {week.map((d, di) => (
                  <td
                    key={di}
                    style={{ height: '100px', verticalAlign: 'top', background: d ? undefined : '#f8f9fa', padding: '4px' }}
                  >
                    {d && <div className="small text-muted mb-1">{d}</div>}
                    {d && (byDay[d] || []).map((b) => (
                      <div
                        key={b.id}
                        onClick={() => openEditModal(b)}
                        title={`${b.guest_name} / ${getStatusLabel(b.booking_status)}`}
                        className={`bg-${getStatusColor(b.booking_status)} text-white`}
                        style={{ cursor: 'pointer', fontSize: '0.72rem', marginTop: 2, padding: '1px 4px', borderRadius: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      >
                        {b.guest_name}
                      </div>
                    ))}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </Table>
        <p className="text-muted small">
          {monthLabel}の予約: {monthCount}件（チェックイン日基準）。色はステータス、クリックで編集できます。
        </p>
      </div>
    );
  };

  if (loading) {
    return (
      <Container className="d-flex justify-content-center" style={{ minHeight: '400px' }}>
        <Spinner animation="border" />
      </Container>
    );
  }

  // 行アクション(テーブル/カードで共用)
  const renderBookingActions = (booking) => (
    <>
      {['pending', 'confirmed', 'queued', 'awaiting_owner'].includes(booking.booking_status) && (
        <Button
          variant="outline-success"
          size="sm"
          onClick={() => handleSendCheckinRequest(booking)}
          title="オーナーのLINEへ承認/却下ボタン付きのチェックイン依頼を送信（順番待ち）"
        >
          {['pending', 'confirmed'].includes(booking.booking_status) ? 'チェックイン依頼' : '依頼を再送'}
        </Button>
      )}
      <Button variant="outline-primary" size="sm" onClick={() => openEditModal(booking)}>編集</Button>
      {booking.booking_status !== 'cancelled' && (
        <Button
          variant="outline-warning"
          size="sm"
          onClick={() => handleCancelBooking(booking)}
          title="予約をキャンセル（承認済みならカレンダー予定も削除）"
        >
          キャンセル
        </Button>
      )}
      <Button variant="outline-danger" size="sm" onClick={() => handleDeleteBooking(booking.id)}>削除</Button>
    </>
  );

  return (
    <Container fluid>
      <div className="page-header">
        <h1 className="page-title">予約管理</h1>
        <Button variant="primary" onClick={openCreateModal}>新規予約</Button>
      </div>

      {message && <Alert variant="success" onClose={() => setMessage('')} dismissible>{message}</Alert>}
      {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}

      <div className="toolbar-row mb-3 d-flex flex-wrap gap-2 align-items-center">
        <Form.Select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="toolbar-control"
          style={{ maxWidth: '220px' }}
        >
          <option value="">すべてのステータス</option>
          <option value="confirmed">確定</option>
          <option value="pending">保留</option>
          <option value="queued">順番待ち</option>
          <option value="awaiting_owner">承認待ち</option>
          <option value="cancelled">キャンセル</option>
          <option value="completed">完了</option>
        </Form.Select>
        <div className="btn-group" role="group" aria-label="表示切替">
          <Button
            variant={viewMode === 'table' ? 'primary' : 'outline-primary'}
            size="sm"
            onClick={() => setViewMode('table')}
          >
            一覧
          </Button>
          <Button
            variant={viewMode === 'calendar' ? 'primary' : 'outline-primary'}
            size="sm"
            onClick={() => setViewMode('calendar')}
          >
            カレンダー
          </Button>
        </div>
        <div className="d-flex gap-1 align-items-center">
          <Form.Control
            type="month"
            size="sm"
            value={exportMonth}
            onChange={(e) => setExportMonth(e.target.value)}
            style={{ maxWidth: '160px' }}
            title="台帳CSVの対象月（未指定なら全期間）"
          />
          <Button
            variant="outline-success"
            size="sm"
            onClick={handleExportCsv}
            title="旅館業法の宿泊者名簿として予約一覧をCSV出力します（月未指定なら全期間）"
          >
            {exportMonth ? `${exportMonth} 台帳CSV` : '台帳CSV出力（全期間）'}
          </Button>
        </div>
      </div>

      {viewMode === 'calendar' && renderCalendar()}

      {viewMode === 'table' && (
      <>
      <div className="d-none d-lg-block">
      <Table striped bordered hover responsive className="data-table">
        <thead>
          <tr>
            {[
              ['予約ID', 'booking_id'],
              ['施設', 'property_name'],
              ['ゲスト', 'guest_name'],
            ].map(([label, key]) => (
              <th key={key} style={{ cursor: 'pointer', whiteSpace: 'nowrap' }} onClick={() => toggleSort(key)}>{label}{sortIndicator(key)}</th>
            ))}
            <th>連絡先</th>
            {[
              ['チェックイン', 'check_in_date'],
              ['チェックアウト', 'check_out_date'],
              ['人数', 'number_of_guests'],
              ['予約状態', 'booking_status'],
              ['支払い', 'payment_status'],
              ['金額', 'total_price'],
            ].map(([label, key]) => (
              <th key={key} style={{ cursor: 'pointer', whiteSpace: 'nowrap' }} onClick={() => toggleSort(key)}>{label}{sortIndicator(key)}</th>
            ))}
            <th>備考</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          {sortedBookings.length === 0 ? (
            <tr>
              <td colSpan={12} className="text-center text-muted">予約はまだ登録されていません</td>
            </tr>
          ) : (
            sortedBookings.map((booking) => (
              <tr key={booking.id}>
                <td>{booking.booking_id}</td>
                <td>{booking.property_name || '-'}</td>
                <td>{booking.guest_name}</td>
                <td>
                  <div>{booking.guest_email}</div>
                  <small className="text-muted">{formatPhone(booking.guest_phone)}</small>
                </td>
                <td>{formatDate(booking.check_in_date)}</td>
                <td>{formatDate(booking.check_out_date)}</td>
                <td>{booking.number_of_guests}</td>
                <td>
                  <span className={`badge bg-${getStatusColor(booking.booking_status)}`}>
                    {getStatusLabel(booking.booking_status)}
                  </span>
                </td>
                <td>
                  <span className={`badge bg-${getStatusColor(booking.payment_status)}`}>
                    {getStatusLabel(booking.payment_status)}
                  </span>
                </td>
                <td>{formatCurrency(booking.total_price)}</td>
                <td title={booking.notes || ''}>{booking.notes ? truncateText(booking.notes, 40) : '-'}</td>
                <td>
                  <div className="row-actions">{renderBookingActions(booking)}</div>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </Table>
      </div>

      {/* モバイル: カード型リスト */}
      <div className="d-lg-none">
        {sortedBookings.length === 0 ? (
          <p className="text-center text-muted">予約はまだ登録されていません</p>
        ) : (
          sortedBookings.map((booking) => (
            <RecordCard
              key={booking.id}
              title={booking.guest_name}
              subtitle={`${booking.property_name || '-'}・${booking.booking_id}`}
              badges={[
                <span key="b" className={`badge bg-${getStatusColor(booking.booking_status)}`}>
                  {getStatusLabel(booking.booking_status)}
                </span>,
                <span key="p" className={`badge bg-${getStatusColor(booking.payment_status)}`}>
                  {getStatusLabel(booking.payment_status)}
                </span>,
              ]}
              fields={[
                { label: 'チェックイン', value: formatDate(booking.check_in_date) },
                { label: 'チェックアウト', value: formatDate(booking.check_out_date) },
                { label: '人数', value: `${booking.number_of_guests}人` },
                { label: '連絡先', value: booking.guest_email || formatPhone(booking.guest_phone) },
                { label: '金額', value: formatCurrency(booking.total_price) },
                { label: '備考', value: booking.notes ? truncateText(booking.notes, 40) : '' },
              ]}
              actions={renderBookingActions(booking)}
            />
          ))
        )}
      </div>
      </>
      )}

      <Modal show={showModal} onHide={closeModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{editingBookingId ? '予約を編集' : '新規予約'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            {/* --- よく使う項目 --- */}
            <Row>
              <Col md={8}>
                <Form.Group className="mb-3">
                  <Form.Label>ゲスト名 <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    name="guest_name"
                    value={bookingForm.guest_name}
                    onChange={handleChange}
                    placeholder="例: 鈴木さま"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>宿泊人数</Form.Label>
                  <Form.Control
                    type="number"
                    min="1"
                    name="number_of_guests"
                    value={bookingForm.number_of_guests}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>チェックイン日 <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="date"
                    name="check_in_date"
                    value={bookingForm.check_in_date}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>チェックアウト日 <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="date"
                    name="check_out_date"
                    value={bookingForm.check_out_date}
                    onChange={handleChange}
                  />
                  <Form.Text muted>{nights > 0 ? `${nights}泊` : ''}</Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>予約媒体</Form.Label>
                  <Form.Select name="booking_source" value={bookingForm.booking_source} onChange={handleChange}>
                    <option value="">選択してください</option>
                    <option value="Airbnb">Airbnb</option>
                    <option value="Agoda">Agoda</option>
                    <option value="Booking.com">Booking.com</option>
                    <option value="楽天トラベル">楽天トラベル</option>
                    <option value="じゃらん">じゃらん</option>
                    <option value="HP">HP（自社サイト）</option>
                    <option value="その他">その他</option>
                    {bookingForm.booking_source &&
                      !['Airbnb', 'Agoda', 'Booking.com', '楽天トラベル', 'じゃらん', 'HP', 'その他'].includes(bookingForm.booking_source) && (
                        <option value={bookingForm.booking_source}>{bookingForm.booking_source}</option>
                      )}
                  </Form.Select>
                  <Form.Text muted>依頼・カレンダー名に使われます</Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>オプション（任意）</Form.Label>
                  <Form.Control
                    type="text"
                    name="option"
                    value={bookingForm.option}
                    onChange={handleChange}
                    placeholder="例: BBQ"
                  />
                  <Form.Text muted>依頼に「BBQ希望」、カレンダーに「（BBQ）」</Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>予約状態</Form.Label>
              <Form.Select name="booking_status" value={bookingForm.booking_status} onChange={handleChange}>
                <option value="pending">保留</option>
                <option value="confirmed">確定</option>
                <option value="awaiting_owner">承認待ち</option>
                <option value="cancelled">キャンセル</option>
                <option value="completed">完了</option>
              </Form.Select>
            </Form.Group>

            {/* --- 詳細(任意) 折りたたみ --- */}
            <Button
              variant="link"
              className="px-0 mb-2"
              onClick={() => setShowDetails((v) => !v)}
            >
              {showDetails ? '▲ 詳細を閉じる' : '▼ 詳細（任意）を入力'}
            </Button>

            {showDetails && (
              <div className="border rounded p-3 mb-2 bg-light">
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>施設名</Form.Label>
                      <Form.Control type="text" name="property_name" value={bookingForm.property_name} onChange={handleChange} />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>予約ID</Form.Label>
                      <Form.Control type="text" name="booking_id" value={bookingForm.booking_id} onChange={handleChange} placeholder="未入力なら自動採番" />
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>メールアドレス</Form.Label>
                      <Form.Control type="email" name="guest_email" value={bookingForm.guest_email} onChange={handleChange} placeholder="guest@example.com" />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>電話番号</Form.Label>
                      <Form.Control type="text" name="guest_phone" value={bookingForm.guest_phone} onChange={handleChange} />
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>玄関パスコード（6桁）</Form.Label>
                      <Form.Control type="text" name="passcode" value={bookingForm.passcode} onChange={handleChange} placeholder="未入力なら承認時に自動採番" />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>1泊料金（円）／合計</Form.Label>
                      <Form.Control type="number" min="0" name="price_per_night" value={bookingForm.price_per_night} onChange={handleChange} placeholder="任意" />
                      <Form.Text muted>{nights}泊 / {formatCurrency(totalPrice)}</Form.Text>
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>支払い状態</Form.Label>
                      <Form.Select name="payment_status" value={bookingForm.payment_status} onChange={handleChange}>
                        <option value="pending">未払い</option>
                        <option value="confirmed">支払い済み</option>
                        <option value="cancelled">返金/取消</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>清掃費（オーナー精算用）</Form.Label>
                      <Form.Control
                        type="number"
                        min="0"
                        name="cleaning_fee"
                        value={bookingForm.cleaning_fee}
                        onChange={handleChange}
                        placeholder="空欄なら既定（精算設定の清掃費）"
                      />
                      <Form.Text muted>例: BBQ清掃 8,000 / 清掃なし 0。空欄は既定値</Form.Text>
                    </Form.Group>
                  </Col>
                </Row>
                <Form.Group className="mb-1">
                  <Form.Label>備考</Form.Label>
                  <Form.Control as="textarea" rows={2} name="notes" value={bookingForm.notes} onChange={handleChange} placeholder="清掃メモ、到着予定、要注意事項など" />
                </Form.Group>
              </div>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeModal}>キャンセル</Button>
          {editingBookingId ? (
            <Button variant="primary" onClick={() => handleSaveBooking(false)} disabled={saving || !canSave}>
              {saving ? '保存中...' : '保存'}
            </Button>
          ) : (
            <>
              <Button variant="outline-secondary" onClick={() => handleSaveBooking(false)} disabled={saving || !canSave}>
                保存のみ
              </Button>
              <Button variant="primary" onClick={() => handleSaveBooking(true)} disabled={saving || !canSave}>
                {saving ? '処理中...' : '保存してオーナーに依頼'}
              </Button>
            </>
          )}
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default Bookings;
